<template>
    <App title="Financier">
        <div class="fin-layout">

            <!-- ── Hero ──────────────────────────────────────────────────── -->
            <div class="fin-hero">
                <div class="fin-avatar">
                    <i class="fas fa-hand-holding-usd"></i>
                </div>
                <div class="fin-hero-info">
                    <h2 class="fin-hero-name">{{ financier.title }}</h2>
                    <span class="fin-badge">Financier</span>
                    <p class="fin-hero-meta">
                        <i class="fas fa-envelope me-1"></i>{{ financier.email || '—' }}
                    </p>
                </div>
                <div class="fin-hero-actions">
                    <a :href="route('financier.page.group')" class="btn btn-sm btn-outline-secondary">
                        <i class="fas fa-arrow-left me-1"></i> Back
                    </a>
                    <button type="button" class="btn btn-sm btn-primary" @click="assignAsset">
                        <i class="fas fa-link me-1"></i> Assign Asset
                    </button>
                    <button type="button" class="btn btn-sm btn-primary" @click="viewAsset">
                        <i class="fas fa-eye me-1"></i> View Records
                    </button>
                </div>
            </div>

            <!-- ── Financier Information ──────────────────────────────────── -->
            <div class="fin-card">
                <div class="fin-card-header">
                    <h3 class="fin-card-title">Financier Information</h3>
                    <a :href="route('financier.page.update', financier.slug)" class="btn btn-sm btn-primary">
                        <i class="fas fa-pencil-alt me-1"></i> Edit
                    </a>
                </div>
                <div class="fin-card-body">
                    <div class="fin-field">
                        <p class="fin-field-label">Title</p>
                        <p class="fin-field-value">{{ financier.title || '—' }}</p>
                    </div>
                    <div class="fin-field">
                        <p class="fin-field-label">Email</p>
                        <p class="fin-field-value">{{ financier.email || '—' }}</p>
                    </div>
                    <div class="fin-field">
                        <p class="fin-field-label">Phone</p>
                        <p class="fin-field-value">{{ financier.phone || '—' }}</p>
                    </div>
                    <div class="fin-field">
                        <p class="fin-field-label">Commission</p>
                        <p class="fin-field-value">
                            <span class="fin-commission-badge">{{ financier.percentage }}%</span>
                        </p>
                    </div>
                    <div class="fin-field">
                        <p class="fin-field-label">Client ID</p>
                        <p class="fin-field-value d-flex align-items-center gap-2">
                            <span>{{ financier.keys.client_id }}</span>
                            <button type="button" class="btn btn-xs " @click="copyToClipboard(financier.keys.client_id, 'Client ID')" title="Copy Client ID">
                                <i class="fas fa-copy fa-sm"></i>
                            </button>
                        </p>
                    </div>
                    <div class="fin-field">
                        <p class="fin-field-label">Client Secret</p>
                        <p class="fin-field-value d-flex align-items-center gap-2">
                            <span>{{ financier.keys.client_secret }}</span>
                            <button type="button" class="btn btn-xs " @click="copyToClipboard(financier.keys.client_secret, 'Client Secret')" title="Copy Client Secret">
                                <i class="fas fa-copy fa-sm"></i>
                            </button>
                        </p>
                    </div>
                    <div class="fin-field">
                        <p class="fin-field-label">Webhook URL</p>
                        <p class="fin-field-value d-flex align-items-center gap-2">
                            <span>{{ financier.webhook || '—' }}</span>
                            <button v-if="financier.webhook" type="button" class="btn btn-xs " @click="copyToClipboard(financier.webhook, 'Webhook URL')" title="Copy Webhook URL">
                                <i class="fas fa-copy fa-sm"></i>
                            </button>
                        </p>
                    </div>
                    <div class="fin-field">
                        <p class="fin-field-label">Webhook Secret</p>
                        <p class="fin-field-value d-flex align-items-center gap-2">
                            <span>{{ financier.secret || '—' }}</span>
                            <button v-if="financier.secret" type="button" class="btn btn-xs " @click="copyToClipboard(financier.secret, 'Webhook Secret')" title="Copy Webhook Secret">
                                <i class="fas fa-copy fa-sm"></i>
                            </button>
                        </p>
                    </div>
                    <div class="fin-field">
                        <p class="fin-field-label">Created</p>
                        <p class="fin-field-value">{{ formatDate(financier.created_at) }}</p>
                    </div>
                </div>
            </div>

            <!-- ── Bank Account ───────────────────────────────────────────── -->
            <div v-if="financier.account" class="fin-card">
                <div class="fin-card-header">
                    <h3 class="fin-card-title"><i class="fas fa-university me-2 text-muted"></i>Bank Account</h3>
                </div>
                <div class="fin-card-body">
                    <div class="fin-field">
                        <p class="fin-field-label">Bank</p>
                        <p class="fin-field-value">{{ financier.account.bank || '—' }}</p>
                    </div>
                    <div class="fin-field">
                        <p class="fin-field-label">Account Number</p>
                        <p class="fin-field-value">{{ financier.account.number || '—' }}</p>
                    </div>
                    <div class="fin-field">
                        <p class="fin-field-label">Account Name</p>
                        <p class="fin-field-value">{{ financier.account.name || '—' }}</p>
                    </div>
                </div>
            </div>

            <!-- ── Asset Bundles ────────────────────────────────────────── -->
            <div class="fin-card">
                <div class="fin-card-header">
                    <h3 class="fin-card-title"><i class="fas fa-layer-group me-2 text-muted"></i>Allocation Records</h3>
                </div>
                <div class="fin-card-body fin-card-body--table">
                    <Datatable :options="bundleOptions" />
                </div>
            </div>

            <!-- ── Admins ────────────────────────────────────────────────── -->
            <div class="fin-card">
                <div class="fin-card-header">
                    <h3 class="fin-card-title"><i class="fas fa-users me-2 text-muted"></i>Admins</h3>
                </div>
                <div class="fin-card-body fin-card-body--table">
                    <Datatable :options="options" />
                </div>
            </div>

        </div>
    </App>
</template>

<script lang="ts">
import type Financier from '@/models/Financier'
import Block from '@/vue/components/Block.vue'
import Datatable from '@/vue/components/Datatable.vue'
import TextBlock from '@/vue/components/TextBlock.vue'
import App from '@/vue/templates/App.vue'
import { CellRender, Column } from '@/vue/utils/Datatable'
import { Link } from '@inertiajs/vue3'
import Swal from 'sweetalert2'
import { Component, Prop, toNative, Vue } from 'vue-facing-decorator'


@Component({
    components: { App, Block, TextBlock, Datatable, Link },
})
class Single extends Vue {
    @Prop({ type: Object })
    readonly financier!: Financier

    formatDate(dateStr: string): string {
        if (!dateStr) return '—'
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
    }

    copyToClipboard(text: string, label: string) {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            Swal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                icon: 'success',
                title: `${label} copied to clipboard!`
            })
        }).catch(() => {
            Swal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                icon: 'error',
                title: `Failed to copy ${label}.`
            })
        })
    }

    assignAsset() {
        this.router.visit(this.route('financier.page.assign', this.financier.slug))
    }

    viewAsset() {
        this.router.visit(this.route('financier.page.assets', this.financier.slug))
    }

    get columns() {
        let columns: any[] = []
        columns.push({
            data: 'id',
            orderable: true,
            name: 'id',
            title: 'Id',
        })
        columns.push({
            data: 'firstname',
            title: 'Firstname',
            orderable: false,
        })
        columns.push({
            data: 'lastname',
            title: 'Lastname',
            orderable: false,
        })
        columns.push({
            data: 'email',
            title: 'Email',
            orderable: false,
        })
        columns.push({
            data: 'created_at',
            title: 'Registered',
            render: CellRender.date(),
            orderable: true,
            name: 'created_at',
        })
        columns.push(Column.actions(this.$gate))
        return columns
    }

    get options() {
        return {
            ajax: {
                url: this.route('financier.ajax.table.admins', { financier: this.financier }),
                method: 'GET',
            },
            columns: this.columns,
        }
    }

    get bundleColumns() {
        return [
            {
                data: 'reference',
                title: 'Reference',
                render: (data: string, type: any, row: any) => {
                    return `<a href="${this.route('bundle.page.single', row.id)}" class="text-primary fw-bold text-decoration-none">${data}</a>`
                }
            },
            {
                data: 'sourceable.title',
                title: 'Source',

            },
            {
                data: 'engines_count',
                title: 'Quantity',
            },
            {
                data: 'admin.fullname',
                title: 'Assigned By',
            },
            {
                data: 'created_at',
                title: 'Date',
                render: CellRender.date(),
            },
            {
                title: 'Action',
                render: (data: any, type: any, row: any) => {
                    return `<a href="${this.route('bundle.page.single', row.id)}" class="btn btn-xs btn-outline-primary">View Receipt</a>`
                }
            }
        ]
    }

    get bundleOptions() {
        return {
            ajax: {
                url: this.route('financier.ajax.table.bundles', { financier: this.financier.slug }),
                method: 'GET',
            },
            columns: this.bundleColumns,
        }
    }

}
export default toNative(Single)
</script>

<style scoped lang="scss">
.fin-layout {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

// ─── Hero ──────────────────────────────────────────────────────────────────
.fin-hero {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    background: #fff;
    border-radius: 12px;
    padding: 1.5rem 1.75rem;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    flex-wrap: wrap;

    @media (max-width: 576px) {
        flex-direction: column;
        align-items: flex-start;
    }
}

.fin-avatar {
    flex-shrink: 0;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1e3a2f, #3a7d52);
    color: #fff;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(30, 58, 47, 0.25);
}

.fin-hero-info {
    flex: 1;
    min-width: 0;
}

.fin-hero-name {
    font-size: 1.25rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.3rem;
    line-height: 1.2;
    word-break: break-word;
}

.fin-badge {
    display: inline-block;
    padding: 0.2rem 0.7rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: #dcfce7;
    color: #15803d;
    margin-bottom: 0.4rem;
}

.fin-hero-meta {
    font-size: 0.85rem;
    color: #6b7280;
    margin: 0.3rem 0 0;
}

.fin-hero-actions {
    flex-shrink: 0;
    margin-left: auto;
    display: flex;
    gap: 0.5rem;

    @media (max-width: 576px) {
        margin-left: 0;
        width: 100%;

        .btn {
            flex: 1;
            justify-content: center;
        }
    }
}

// ─── Card ──────────────────────────────────────────────────────────────────
.fin-card {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

.fin-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.1rem 1.5rem;
    border-bottom: 1px solid #f1f3f5;
}

.fin-card-title {
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
}

.fin-card-body {
    display: grid;
    grid-template-columns: repeat(3, 1fr);

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }

    &--table {
        display: block;
        padding: 1.25rem 1.5rem;
    }

    &--list {
        display: flex;
        flex-direction: column;
        padding: 0;
    }
}

.fin-field {
    padding: 1.25rem 1.5rem;
    border-right: 1px solid #f1f3f5;
    border-bottom: 1px solid #f1f3f5;

    &:nth-child(3n) {
        border-right: none;
    }
}

.fin-field-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #9ca3af;
    margin-bottom: 0.35rem;
}

.fin-field-value {
    font-size: 0.95rem;
    font-weight: 500;
    color: #111827;
    margin: 0;
    word-break: break-word;
}

.fin-commission-badge {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 700;
    background: #dcfce7;
    color: #15803d;
}
</style>
